import { el, render } from "../dom.ts";
import { icon } from "../icons.ts";
import { activeProvider, hasCredential, providerLabel } from "../../core/ai/config.ts";
import { diagnose, type Guess } from "../../core/ai/inquire.ts";
import {
  COMMON, DIAGNOSIS_RULE, FRAMING, NATURES, NOT_A_TYPE,
  SITUATED_HEAD, SYNTHESIS_HEAD, commonHead, natureOf,
  type Nature, type NatureId,
} from "../../core/method/indagar.ts";
import type { Route, RouteStep } from "../../core/method/route.ts";

/**
 * El mapa de la ruta: nodos, aristas y la bifurcación.
 *
 * Se pidió «una conexión por nodos para saber cuál camino se tomó», poder
 * «seleccionar el camino antes de dejarlo» y un botón que muestre «el camino
 * que estamos recorriendo como un mapa». Esto es eso, y su forma no es
 * decorativa: Indagar es la única fase con una ruta que se bifurca, y una
 * bifurcación no se entiende en una lista. Cuatro pasos comunes, un abanico de
 * siete naturalezas, cinco pasos de la que se tome y una salida común.
 *
 * Las tres cosas que hace y que una lista no haría:
 *
 * - **Muestra dónde se está.** Los nodos se pintan con lo que dice el
 *   documento: hecho, en curso, por recorrer. No hay un progreso guardado en
 *   otro sitio que pudiera contradecirlo.
 * - **Deja probar antes de decidir.** Pulsar una naturaleza la previsualiza:
 *   se ve su rama entera, su pregunta central y sus herramientas, y el
 *   documento sigue intacto. Tomarla es un segundo gesto, aparte y con nombre.
 * - **Deja cambiar de ruta.** Cambiar no castiga: lo escrito en la rama
 *   anterior se conserva —lo hace `withNature`—, y aquí se dice antes de
 *   pulsar, que es cuando importa saberlo.
 *
 * La regla del método manda sobre el mapa y está escrita arriba: la naturaleza
 * no se elige por intuición, se infiere de la evidencia. Por eso el botón de la
 * IA de esta pantalla **ordena y cita**, y no toma la ruta: pulsar lo que la
 * evidencia sostiene sigue siendo un gesto de la persona.
 */

/** Cómo se pinta una arista: fuera de la ruta, en la ruta, o la que corre. */
type Flow = "off" | "on" | "flow";

export interface MapOptions {
  route: () => Route;
  content: () => string;
  intent: () => string | null;
  /** Tomar una ruta: el documento se reescribe con esa naturaleza dentro. */
  onTake: (id: NatureId) => void;
  /** Ir a un paso del documento. Cierra el mapa. */
  onGo: (step: RouteStep) => void;
  onAsk: (question: string) => void;
}

export interface RouteMap {
  root: HTMLElement;
  button: HTMLButtonElement;
  open: () => void;
  toggle: () => void;
  close: () => void;
  /** Vuelve a leer la ruta del documento. Sin efecto con el mapa cerrado. */
  repaint: () => void;
}

/* --- la retícula del lienzo ----------------------------------------------- */

const NODE_W = 210;
/** El alto mínimo de un nodo, y el que se supone si no se puede medir. */
const NODE_H = 64;
/** Separación entre columnas, del borde izquierdo al borde izquierdo siguiente. */
const COL = 260;
/** El aire entre dos nodos de la misma columna. */
const GAP = 18;
const PAD = 20;

/** Las ocho columnas: cuatro comunes, el abanico, la rama y las dos salidas. */
const COLS = 8;

const ZOOM_MIN = 0.35;
const ZOOM_MAX = 1.6;

/** Un nodo puesto: dónde está y **cuánto ocupa de verdad**. */
interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Dot {
  /** Identidad para las aristas. */
  key: string;
  /** La columna donde cae. El sitio exacto se calcula al pintar, no aquí. */
  col: number;
  kind: "paso" | "naturaleza" | "salida";
  head: string;
  say: string;
  state: "done" | "now" | "todo" | "ghost" | "taken" | "pick";
  /** El número que lleva la chapa, si lleva. */
  tag?: string;
  onPick?: () => void;
  title?: string;
}

/**
 * Dónde va cada nodo, **medido y no supuesto**.
 *
 * Antes el alto era la constante `NODE_H` y las filas iban a un paso fijo. Pero
 * un nodo crece con su texto: «Potencial no activado» ocupa tres líneas y
 * «Tensión» una, y el paso fijo no lo sabía. Los nodos altos se metían encima
 * del de abajo —se veía en el abanico de siete y en la rama— y, lo que es peor,
 * las aristas salían a media altura de un alto que nadie tenía: por eso las
 * curvas llegaban torcidas al elegir una naturaleza.
 *
 * Se mide lo que el navegador ya pintó (`offsetHeight`) y se apila con aire de
 * verdad. Cada columna se centra sobre el mismo eje, así que la fila común
 * sigue leyéndose como una fila aunque el abanico crezca.
 */
function layout(dots: readonly Dot[], heights: ReadonlyMap<string, number>): {
  spots: Map<string, Box>;
  plane: { w: number; h: number };
} {
  const byCol = new Map<number, Dot[]>();
  for (const one of dots) {
    const list = byCol.get(one.col);
    if (list) list.push(one);
    else byCol.set(one.col, [one]);
  }

  const high = (one: Dot): number => Math.max(heights.get(one.key) ?? NODE_H, NODE_H);
  const tall = (list: readonly Dot[]): number =>
    list.reduce((sum, one) => sum + high(one), 0) + Math.max(list.length - 1, 0) * GAP;

  // El eje: la mitad de la columna más alta. Se calcula antes de colocar nada,
  // porque todas las columnas se centran sobre él.
  const highest = Math.max(...[...byCol.values()].map((list) => tall(list)), NODE_H);
  const mid = PAD + highest / 2;

  const spots = new Map<string, Box>();
  for (const [col, list] of byCol) {
    let y = mid - tall(list) / 2;
    for (const one of list) {
      const h = high(one);
      spots.set(one.key, { x: PAD + col * COL, y, w: NODE_W, h });
      y += h + GAP;
    }
  }

  return {
    spots,
    plane: { w: PAD + (COLS - 1) * COL + NODE_W + PAD, h: highest + PAD * 2 },
  };
}

export function mountMap(options: MapOptions): RouteMap {
  /** La naturaleza que se está mirando sin haberla tomado. */
  let picked: NatureId | null = null;
  let guesses: readonly Guess[] = [];
  let missing: readonly string[] = [];
  let asking = false;
  let failed = "";
  let running: AbortController | null = null;

  let zoom = 0.62;
  let tx = 0;
  let ty = 0;

  const edges = svg("svg", { class: "map__edges" });
  const nodes = el("div", { class: "map__nodes" });
  const canvas = el("div", { class: "map__canvas" }, [edges, nodes]);
  const stage = el("div", { class: "map__stage", attrs: { tabindex: "0" } }, [canvas]);
  const aside = el("aside", { class: "map__side" });
  /* La franja de la decisión. Estaba abajo y la hoja del proyecto se le ponía
     encima: el botón de tomar la ruta quedaba debajo de otra cosa, que es como
     no estar. Arriba, pegada a la barra, no hay nada que pueda taparla. */
  const deal = el("div", { class: "map__deal" });
  /** El zoom, en letra: un número que se lee dice más que tres botones. */
  const readout = el("span", { class: "map__zn", attrs: { "aria-hidden": "true" } });
  /** Lo que mide el lienzo ahora mismo, que lo decide lo que se pintó. */
  let field = { w: PAD * 2 + NODE_W, h: PAD * 2 + NODE_H };

  const button = el("button", {
    class: "wr__act",
    text: "Mapa",
    attrs: {
      type: "button",
      title: "Ver la ruta de Indagar como un mapa",
      "aria-expanded": "false",
      "aria-controls": "wr-map",
    },
    on: { click: () => { toggle(); } },
  });

  const root = el("section", {
    class: "map",
    attrs: { hidden: true, "aria-label": "Mapa de la ruta", id: "wr-map" },
  }, [
    el("header", { class: "map__bar" }, [
      icon("hub", "ico"),
      el("span", { class: "map__title", text: "La ruta de Indagar" }),
      el("span", { class: "map__rule", text: DIAGNOSIS_RULE }),
      el("div", { class: "map__zoom" }, [
        zoomer("minus", "Alejar", () => { scale(zoom - 0.12); }),
        readout,
        zoomer("plus", "Acercar", () => { scale(zoom + 0.12); }),
        el("span", { class: "map__zoom__bar" }),
        zoomer("fit", "Ajustar a la pantalla", () => { fit(); }),
      ]),
      el("button", {
        class: "map__x",
        attrs: { type: "button", "aria-label": "Cerrar el mapa" },
        on: { click: () => { close(); } },
      }, [icon("cross", "ico ico--small")]),
    ]),
    deal,
    el("div", { class: "map__body" }, [stage, aside]),
  ]);

  function zoomer(glyph: "minus" | "plus" | "fit", title: string, run: () => void): HTMLElement {
    return el("button", {
      class: "map__zb",
      attrs: { type: "button", title, "aria-label": title },
      on: { click: () => { run(); } },
    }, [icon(glyph, "ico ico--small")]);
  }

  /* --- el grafo ----------------------------------------------------------- */

  /**
   * Los nodos con sus sitios y su estado.
   *
   * El estado sale del documento y no de aquí: `route` ya sabe qué pasos están
   * hechos y cuál toca. Lo único que este módulo añade es la naturaleza que se
   * está mirando sin haberla tomado, que por definición todavía no está escrita
   * en ninguna parte.
   */
  function graph(route: Route): Dot[] {
    const out: Dot[] = [];
    const shown = picked ?? route.nature?.id ?? null;
    const nature = shown ? natureOf(shown) : null;

    COMMON.forEach((step, index) => {
      const head = commonHead(step);
      const live = route.steps.find((one) => one.head === head);
      out.push({
        key: `comun-${step.id}`,
        col: index,
        kind: "paso",
        head,
        say: step.output,
        state: state(live, route),
        tag: String(step.n),
        title: `${step.action}. Avanza cuando: ${step.criterion}.`,
        ...(live ? { onPick: () => { close(); options.onGo(live); } } : {}),
      });
    });

    NATURES.forEach((one) => {
      const taken = route.nature?.id === one.id;
      const rank = guesses.findIndex((guess) => guess.nature === one.id);
      out.push({
        key: `nat-${one.id}`,
        col: 4,
        kind: "naturaleza",
        head: one.name,
        say: one.question,
        state: taken ? "taken" : picked === one.id ? "pick" : "todo",
        ...(rank >= 0 ? { tag: `${rank + 1}ª` } : {}),
        title: one.question,
        onPick: () => { picked = picked === one.id ? null : one.id; paint(); },
      });
    });

    const steps = nature?.steps ?? [];
    if (nature && steps.length > 0) {
      steps.forEach((step) => {
        const live = route.nature?.id === nature.id
          ? route.steps.find((one) => one.n === step.n && one.part === "naturaleza")
          : undefined;
        out.push({
          key: `ram-${nature.id}-${step.n}`,
          col: 5,
          kind: "paso",
          head: step.name,
          say: step.ask,
          state: route.nature?.id === nature.id ? state(live, route) : "ghost",
          tag: String(step.n),
          title: step.ask,
          ...(live ? { onPick: () => { close(); options.onGo(live); } } : {}),
        });
      });
    } else {
      // Sin naturaleza no hay rama que dibujar, y dejar el hueco vacío haría
      // pensar que la ruta se acaba en el abanico. Se pone un nodo que dice
      // exactamente qué falta.
      out.push({
        key: "ram-vacia",
        col: 5,
        kind: "paso",
        head: "Cinco pasos",
        say: "elige una naturaleza para verlos",
        state: "ghost",
      });
    }

    const synth = route.steps.find((one) => one.id === "sintesis");
    const situated = route.steps.find((one) => one.id === "situada");
    out.push({
      key: "out-sintesis",
      col: 6,
      kind: "salida",
      head: SYNTHESIS_HEAD,
      say: "los siete puntos de la situación",
      state: state(synth, route),
      ...(synth ? { onPick: () => { close(); options.onGo(synth); } } : {}),
    });
    out.push({
      key: "out-situada",
      col: 7,
      kind: "salida",
      head: SITUATED_HEAD,
      say: "pasa a Idear",
      state: state(situated, route),
      ...(situated ? { onPick: () => { close(); options.onGo(situated); } } : {}),
    });

    return out;
  }

  function state(step: RouteStep | undefined, route: Route): Dot["state"] {
    if (!step) return "ghost";
    if (step.done) return "done";
    if (route.next && route.next.id === step.id) return "now";
    return "todo";
  }

  /**
   * Las aristas: de qué nodo a qué nodo, y cómo se pintan.
   *
   * Tres estados, no dos. «off» es un camino posible que nadie ha tomado; «on»
   * es por donde pasa la ruta; «flow» es **la arista que llega a donde se está
   * ahora mismo**, y ésa se anima. La animación no decora: en un grafo de
   * catorce nodos, el que dice «estás aquí» tiene que encontrarse sin leer, y
   * un trazo que corre se encuentra antes que un borde más gordo.
   *
   * «flow» lleva además la clase de «on»: la ruta sí pasa por ahí, y quien
   * cuente el camino encendido tiene que contarla.
   */
  function wires(route: Route, all: readonly Dot[]): [string, string, Flow][] {
    const shown = picked ?? route.nature?.id ?? null;
    const nature = shown ? natureOf(shown) : null;
    const live = (id: string): boolean => shown !== null && id === shown;

    // El nodo donde se está: la arista que le llega es la que corre.
    const now = all.find((one) => one.state === "now")?.key ?? null;
    const flow = (from: string, to: string, on: boolean): [string, string, Flow] =>
      [from, to, on ? (to === now ? "flow" : "on") : "off"];

    const out: [string, string, Flow][] = [];
    for (let index = 0; index < COMMON.length - 1; index += 1) {
      const from = COMMON[index]?.id;
      const to = COMMON[index + 1]?.id;
      if (from && to) out.push(flow(`comun-${from}`, `comun-${to}`, true));
    }

    // El abanico: cuatro pasos comunes y de ahí siete caminos posibles. Es la
    // imagen de la lámina —«desde aquí la ruta se bifurca»— y es lo único de
    // este mapa que no se puede decir con una línea recta.
    for (const one of NATURES) out.push(flow("comun-diagnostico", `nat-${one.id}`, live(one.id)));

    if (nature) {
      const steps = nature.steps;
      out.push(flow(`nat-${nature.id}`, `ram-${nature.id}-${steps[0]?.n ?? 1}`, true));
      for (let index = 0; index < steps.length - 1; index += 1) {
        out.push(flow(
          `ram-${nature.id}-${steps[index]?.n ?? 0}`,
          `ram-${nature.id}-${steps[index + 1]?.n ?? 0}`,
          true,
        ));
      }
      out.push(flow(`ram-${nature.id}-${steps[steps.length - 1]?.n ?? 5}`, "out-sintesis", true));
    } else {
      out.push(flow("ram-vacia", "out-sintesis", false));
    }
    out.push(flow("out-sintesis", "out-situada", true));

    const known = new Set(all.map((one) => one.key));
    return out.filter(([from, to]) => known.has(from) && known.has(to));
  }

  /* --- pintar ------------------------------------------------------------- */

  /**
   * Pintar, en dos pasadas: primero los nodos para poder medirlos, después las
   * aristas con las medidas de verdad.
   *
   * El orden no es un capricho. El alto de un nodo lo decide su texto, y sólo
   * el navegador sabe cuánto ocupa una vez pintado. Una sola pasada tendría que
   * suponerlo, y suponer es lo que dejaba nodos encima de otros y curvas
   * llegando a media altura de una caja que no medía eso.
   *
   * Se leen todos los altos seguidos y después se escriben todas las
   * posiciones: así el navegador recalcula la página una vez y no catorce.
   */
  function paint(): void {
    const route = options.route();
    const all = graph(route);

    // Primera pasada: los nodos existen y tienen ancho, todavía sin sitio.
    const made = new Map(all.map((one) => [one.key, node(one)] as const));
    render(nodes, ...made.values());

    // Medir. Con el mapa cerrado todo mide cero: entonces vale el alto mínimo,
    // y al abrirse se vuelve a pintar con las medidas buenas.
    const heights = new Map<string, number>();
    for (const [key, box] of made) heights.set(key, box.offsetHeight);

    const { spots, plane } = layout(all, heights);

    // Segunda pasada: colocar.
    for (const [key, box] of made) {
      const at = spots.get(key);
      if (!at) continue;
      box.style.left = `${at.x}px`;
      box.style.top = `${at.y}px`;
    }

    canvas.style.width = `${plane.w}px`;
    canvas.style.height = `${plane.h}px`;
    edges.setAttribute("viewBox", `0 0 ${plane.w} ${plane.h}`);
    edges.setAttribute("width", String(plane.w));
    edges.setAttribute("height", String(plane.h));
    field = plane;

    const drawn = wires(route, all).flatMap(([from, to, how]) => {
      const a = spots.get(from);
      const b = spots.get(to);
      if (!a || !b) return [];
      return [svg("path", {
        class: `map__wire${how === "off" ? "" : " is-on"}${how === "flow" ? " is-flow" : ""}`,
        d: curve(a, b),
      })];
    });

    // El cerco de donde se está, alrededor del nodo y no dentro: un rectángulo
    // de puntos que corren. Va en el SVG porque un borde de CSS no se puede
    // poner a andar, y así sigue la forma exacta del nodo, esquinas incluidas.
    const here = all.find((one) => one.state === "now");
    const at = here ? spots.get(here.key) : undefined;
    if (at) {
      drawn.push(svg("rect", {
        class: "map__ants",
        x: String(at.x - 4),
        y: String(at.y - 4),
        width: String(at.w + 8),
        height: String(at.h + 8),
        rx: "5",
      }));
    }

    render(edges, ...drawn);
    render(aside, ...side(route));
    render(deal, ...decide(route));
    place();
  }

  function node(one: Dot): HTMLElement {
    const kids: (Node | string)[] = [];
    if (one.tag) kids.push(el("span", { class: "map__n__tag", text: one.tag }));
    kids.push(el("span", { class: "map__n__head", text: one.head }));
    kids.push(el("span", { class: "map__n__say", text: one.say }));
    if (one.state === "done") kids.push(icon("check", "ico ico--twist map__n__ok"));

    const tag = one.onPick ? "button" : "div";
    const box = el(tag as "button", {
      class: `map__n map__n--${one.kind} is-${one.state}`,
      attrs: {
        ...(one.onPick ? { type: "button" } : {}),
        ...(one.title ? { title: one.title } : {}),
      },
      ...(one.onPick ? { on: { click: () => { one.onPick?.(); } } } : {}),
    }, kids);

    // El sitio lo pone el pintado, después de medir. Aquí sólo el ancho, que
    // es el que hace que el texto se parta y decida el alto.
    box.style.width = `${NODE_W}px`;
    box.style.minHeight = `${NODE_H}px`;
    return box;
  }

  /**
   * La columna del lado: qué es la naturaleza que se está mirando, y lo que la
   * lámina dice sobre elegirla.
   */
  function side(route: Route): HTMLElement[] {
    const shown = picked ?? route.nature?.id ?? null;
    const nature = shown ? natureOf(shown) : null;
    const out: HTMLElement[] = [];

    out.push(el("div", { class: "map__where" }, [
      el("span", { class: "map__where__say", text: whereSays(route) }),
      el("span", { class: "map__where__n", text: `${route.done} de ${route.total} pasos` }),
      bar(route.done, route.total),
    ]));

    if (nature) out.push(natureCard(nature, route));

    out.push(el("div", { class: "map__ai" }, aiBits()));

    out.push(el("div", { class: "map__notes" }, [
      el("span", { class: "map__notes__title", text: "Cómo se lee este mapa" }),
      el("ul", { class: "map__notes__list" }, [
        ...FRAMING.map((one) => el("li", { text: one })),
        el("li", { text: NOT_A_TYPE }),
      ]),
    ]));

    return out;
  }

  function natureCard(nature: Nature, route: Route): HTMLElement {
    const taken = route.nature?.id === nature.id;
    return el("div", { class: `map__card${taken ? " is-taken" : ""}` }, [
      el("span", { class: "map__card__kind", text: taken ? "Ruta tomada" : "Estás mirando" }),
      el("span", { class: "map__card__head", text: nature.name }),
      el("p", { class: "map__card__ask", text: nature.question }),
      ...(nature.combined
        ? [el("p", { class: "map__card__mix", text:
            "Varias naturalezas trenzadas: se nombran todas y se ordena por dónde empezar." })]
        : []),
      el("p", { class: "map__card__tools" }, [
        el("span", { class: "map__card__label", text: "Herramientas sugeridas" }),
        el("span", { text: nature.tools.join(" · ") }),
        el("span", { class: "map__card__soft", text: "Orientativas, no obligatorias." }),
      ]),
      el("p", { class: "map__card__out" }, [
        el("span", { class: "map__card__label", text: "Termina en" }),
        el("span", { text: nature.output }),
      ]),
      ...guessOf(nature.id),
    ]);
  }

  /** Lo que el modelo dijo de esta naturaleza, si dijo algo. */
  function guessOf(id: NatureId): HTMLElement[] {
    const found = guesses.find((one) => one.nature === id);
    if (!found) return [];
    const rank = guesses.indexOf(found) + 1;
    return [el("div", { class: "map__why" }, [
      el("span", { class: "map__why__title", text: `Lectura ${rank}ª según la evidencia` }),
      el("p", { class: "map__why__say", text: found.why }),
      ...(found.evidence
        ? [el("p", { class: "map__why__ev", text: `«${found.evidence}»` })]
        : []),
    ])];
  }

  /** El botón de diagnosticar y lo que devolvió. Ordena; no toma la ruta. */
  function aiBits(): HTMLElement[] {
    if (asking) {
      return [el("p", { class: "map__busy" }, [
        el("span", { class: "prop__dot" }), "Leyendo la evidencia del documento…",
      ])];
    }

    const out: HTMLElement[] = [];
    if (failed) out.push(el("p", { class: "map__bad", text: failed }));

    out.push(el("button", {
      class: "btn btn--quiet",
      attrs: { type: "button" },
      on: { click: () => { void run(); } },
    }, [
      icon("aim", "ico ico--small"),
      el("span", { text: guesses.length > 0 ? "Volver a diagnosticar" : "Diagnosticar con la evidencia" }),
    ]));

    if (guesses.length === 0) {
      out.push(el("p", { class: "map__soft", text:
        "Lee lo escrito y ordena las naturalezas que el documento sostiene, con el " +
        "fragmento en que se apoya cada una. No toma la ruta: eso lo haces tú." }));
      return out;
    }

    out.push(el("ul", { class: "map__rank" }, guesses.map((one, index) => {
      const nature = natureOf(one.nature);
      return el("li", {}, [
        el("button", {
          class: `map__rank__b${picked === one.nature ? " is-on" : ""}`,
          attrs: { type: "button", title: one.why },
          on: { click: () => { picked = one.nature; paint(); } },
        }, [
          el("span", { class: "map__rank__n", text: `${index + 1}ª` }),
          el("span", { class: "map__rank__name", text: nature?.name ?? one.nature }),
        ]),
      ]);
    })));

    if (missing.length > 0) {
      out.push(el("div", { class: "map__gaps" }, [
        el("span", { class: "map__gaps__title", text: "Para diagnosticar con seguridad falta" }),
        el("ul", {}, missing.map((one) => el("li", { text: one }))),
      ]));
    }

    out.push(el("button", {
      class: "lnkbtn",
      text: "Discutir el diagnóstico",
      attrs: { type: "button" },
      on: {
        click: () => {
          const said = guesses.map((one, index) =>
            `${index + 1}. ${natureOf(one.nature)?.name ?? one.nature}: ${one.why}`).join("\n");
          close();
          options.onAsk(
            "Sobre el diagnóstico de la naturaleza de mi situación, la IA ordenó así " +
            `las lecturas posibles:\n${said}\n\n¿Qué evidencia de mi documento le falta ` +
            "a la primera para sostenerse, y qué me convendría mirar antes de tomar la ruta?",
          );
        },
      },
    }));

    return out;
  }

  /** La franja de la decisión: tomar la ruta, cambiarla, o nada. */
  function decide(route: Route): HTMLElement[] {
    const taken = route.nature;
    if (!picked || picked === taken?.id) {
      if (!taken) {
        return [el("p", { class: "map__deal__say", text:
          "Pulsa una naturaleza para ver su rama entera. El documento no se toca hasta " +
          "que tomes la ruta." })];
      }
      return [el("p", { class: "map__deal__say", text:
        `La ruta de este documento es «${taken.name}». Pulsa otra naturaleza para ver ` +
        "a dónde llevaría." })];
    }

    const nature = natureOf(picked);
    if (!nature) return [];

    return [
      el("p", { class: "map__deal__say", text: taken
        ? `Cambiar de «${taken.name}» a «${nature.name}». Lo que ya escribiste en la rama ` +
          "anterior se queda en el documento: no se borra nada."
        : `Tomar la ruta de «${nature.name}»: sus cinco pasos entran en el documento con ` +
          "sus preguntas dentro." }),
      el("div", { class: "map__deal__acts" }, [
        el("button", {
          class: "btn",
          text: taken ? `Cambiar a ${nature.name}` : `Tomar la ruta de ${nature.name}`,
          attrs: { type: "button" },
          on: {
            click: () => {
              const id = nature.id;
              picked = null;
              options.onTake(id);
              paint();
            },
          },
        }),
        el("button", {
          class: "lnkbtn",
          text: "Seguir mirando",
          attrs: { type: "button" },
          on: { click: () => { picked = null; paint(); } },
        }),
      ]),
    ];
  }

  function whereSays(route: Route): string {
    switch (route.stage) {
      case "comun": return `Estás en ${route.next?.head ?? "los pasos comunes"}.`;
      case "diagnostico": return "Los tres primeros pasos están: toca diagnosticar la naturaleza.";
      case "naturaleza": return `Recorriendo la rama: ${route.next?.head ?? ""}.`;
      case "salida": return "La rama está recorrida: queda cerrar la salida común.";
      case "listo": return "La ruta está recorrida entera.";
    }
  }

  function bar(done: number, total: number): HTMLElement {
    const fill = el("span", { class: "map__track__fill" });
    fill.style.width = `${Math.round((done / Math.max(total, 1)) * 100)}%`;
    return el("span", {
      class: "map__track",
      attrs: {
        role: "progressbar",
        "aria-valuenow": done,
        "aria-valuemin": 0,
        "aria-valuemax": total,
      },
    }, [fill]);
  }

  /* --- diagnosticar ------------------------------------------------------- */

  async function run(): Promise<void> {
    if (asking) return;
    const provider = activeProvider();
    if (!hasCredential(provider)) {
      failed = `Para diagnosticar hace falta la credencial de ${providerLabel(provider)}. ` +
        "Se pone en la bandeja de la franja, en «IA».";
      paint();
      return;
    }

    running?.abort();
    const controller = new AbortController();
    running = controller;
    asking = true;
    failed = "";
    paint();

    try {
      const read = await diagnose({
        content: options.content(),
        intent: options.intent(),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      guesses = read.ranked;
      missing = read.missing;
      // Se mira la primera, no se toma: leer la lectura mejor sostenida es el
      // gesto siguiente, y tomarla sigue siendo el de después.
      picked = read.ranked[0]?.nature ?? null;
    } catch (cause) {
      if (controller.signal.aborted) return;
      failed = cause instanceof Error ? cause.message : "No se pudo diagnosticar.";
    } finally {
      if (running === controller) {
        running = null;
        asking = false;
        paint();
      }
    }
  }

  /* --- mover el lienzo ---------------------------------------------------- */

  function place(): void {
    const size = Math.round(zoom * 1000) / 1000;
    canvas.style.transform = `translate(${Math.round(tx)}px, ${Math.round(ty)}px) scale(${size})`;
    readout.textContent = `${Math.round(zoom * 100)}%`;
  }

  /** Acercar o alejar desde los botones: el centro de la pantalla manda. */
  function scale(next: number): void {
    const box = stage.getBoundingClientRect();
    scaleAt(next, box.left + box.width / 2, box.top + box.height / 2);
  }

  /**
   * Acercar hacia un punto de la pantalla, no hacia el centro.
   *
   * Se pasa el punto del escenario a coordenadas del lienzo, se cambia la
   * escala y se recoloca para que ese mismo punto del lienzo siga cayendo
   * debajo del puntero. Sin esto, acercarse con la rueda a un nodo del borde lo
   * echa fuera de la pantalla y hay que volver a buscarlo arrastrando.
   */
  function scaleAt(next: number, screenX: number, screenY: number): void {
    const before = zoom;
    const after = clampZoom(next);
    if (after === before) return;
    const box = stage.getBoundingClientRect();
    const atX = screenX - box.left;
    const atY = screenY - box.top;
    // Dónde cae ese punto dentro del lienzo, antes de mover nada.
    const onX = (atX - tx) / before;
    const onY = (atY - ty) / before;
    zoom = after;
    tx = atX - onX * after;
    ty = atY - onY * after;
    place();
  }

  /* Sin redondear: un panel táctil manda pasos diminutos, y cortar a dos
     decimales los dejaría todos en cero —el mapa no se movería nunca—. Lo que
     se pinta sí se redondea, en `place()`. */
  function clampZoom(next: number): number {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
  }

  /** Todo el grafo dentro de la pantalla, centrado. */
  function fit(): void {
    const box = stage.getBoundingClientRect();
    if (box.width < 40) return;
    const margin = 28;
    zoom = clampZoom(Math.min(
      (box.width - margin * 2) / field.w,
      (box.height - margin * 2) / field.h,
    ));
    tx = (box.width - field.w * zoom) / 2;
    ty = (box.height - field.h * zoom) / 2;
    place();
  }

  // Arrastrar el fondo mueve el lienzo. Sobre un nodo no: ahí el gesto es
  // pulsarlo, y un mapa que se desplaza al intentar elegir una ruta se pelea
  // con quien lo usa.
  let drag: { x: number; y: number; tx: number; ty: number } | null = null;
  stage.addEventListener("pointerdown", (event) => {
    if (event.target instanceof Element && event.target.closest(".map__n")) return;
    drag = { x: event.clientX, y: event.clientY, tx, ty };
    stage.setPointerCapture(event.pointerId);
    stage.classList.add("is-dragging");
  });
  stage.addEventListener("pointermove", (event) => {
    if (!drag) return;
    tx = drag.tx + (event.clientX - drag.x);
    ty = drag.ty + (event.clientY - drag.y);
    place();
  });
  const letGo = (): void => { drag = null; stage.classList.remove("is-dragging"); };
  stage.addEventListener("pointerup", letGo);
  stage.addEventListener("pointercancel", letGo);

  /* La rueda acerca y aleja, sin tecla de por medio: el escenario no tiene nada
     que desplazar —se mueve arrastrando—, así que la rueda no le hace falta
     para otra cosa. Se acerca hacia el puntero, que es donde se está mirando.
     El pellizco del panel táctil llega como rueda con Ctrl y hace lo mismo. */
  stage.addEventListener("wheel", (event) => {
    event.preventDefault();
    // El pellizco manda pasos grandes y la rueda pasos de línea; se igualan
    // para que ninguno de los dos se pase de largo con un solo gesto.
    const step = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    scaleAt(zoom * Math.exp(-step * 0.0016), event.clientX, event.clientY);
  }, { passive: false });

  /**
   * Al cambiar de tamaño la ventana, que siga habiendo algo que mirar.
   *
   * El encaje sólo se hacía al abrir. Después, estrechar la ventana dejaba la
   * escala y el desplazamiento de antes, y el grafo podía acabar entero fuera
   * del escenario: el mapa abierto y nada dentro. No se rehace el encaje —si
   * alguien se acercó a leer un nodo, ese acercamiento es suyo— sino que se
   * ata el desplazamiento a que el lienzo siga asomando.
   */
  function clampPan(): void {
    const box = stage.getBoundingClientRect();
    if (box.width < 40) return;
    const wide = field.w * zoom;
    const high = field.h * zoom;
    // Cuánto tiene que seguir asomando: un trozo de verdad, no un píxel.
    const keep = 80;
    tx = wide <= box.width
      ? (box.width - wide) / 2
      : Math.min(Math.max(tx, box.width - wide - keep), keep);
    ty = high <= box.height
      ? (box.height - high) / 2
      : Math.min(Math.max(ty, box.height - high - keep), keep);
    place();
  }

  /* Cerrado no se mide: el escenario no tiene tamaño y la cuenta saldría con
     las medidas de la pantalla anterior. Se espera un fotograma porque al
     llegar el aviso la página todavía no ha recolocado nada. */
  window.addEventListener("resize", () => {
    if (root.hidden) return;
    requestAnimationFrame(() => { clampPan(); });
  });

  /* --- abrir, cerrar ------------------------------------------------------ */

  function open(): void {
    if (!root.hidden) return;
    root.hidden = false;
    button.setAttribute("aria-expanded", "true");
    picked = null;
    failed = "";
    paint();
    // Ajustar después de pintar: antes el escenario no tiene tamaño y el
    // encaje saldría con las medidas de la pantalla anterior.
    requestAnimationFrame(() => { fit(); stage.focus(); });
  }

  function close(): void {
    if (root.hidden) return;
    running?.abort();
    running = null;
    asking = false;
    root.hidden = true;
    button.setAttribute("aria-expanded", "false");
  }

  function toggle(): void {
    if (root.hidden) open();
    else close();
  }

  /**
   * El mapa detrás del documento, cuando el documento cambia.
   *
   * Se puede escribir con el mapa abierto —está al lado, no encima—, y entonces
   * el avance que dibuja tiene que ser el de ahora mismo: responder la pregunta
   * de un paso lo da por hecho, y un mapa que siguiera diciendo lo de antes
   * estaría contando otra cosa que la que hay escrita. Cerrado no se pinta: no
   * hay nada que mirar y el documento se escribe más veces de lo que se abre.
   */
  function repaint(): void {
    if (root.hidden) return;
    paint();
  }

  return { root, button, open, toggle, close, repaint };
}

/* --- las tripas ----------------------------------------------------------- */

/** Un nodo SVG, que `el()` no sabe hacer: necesita su espacio de nombres. */
function svg(tag: string, attrs: Record<string, string>): SVGElement {
  const node = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [name, value] of Object.entries(attrs)) {
    if (name === "class") node.setAttribute("class", value);
    else node.setAttribute(name, value);
  }
  return node;
}

/**
 * La arista entre dos nodos, como en la referencia: una curva de Bézier de
 * trazo discontinuo.
 *
 * Sale por el lado que corresponde —derecha si el siguiente está a la derecha,
 * abajo si está debajo— porque una línea que sale por donde no toca hace pensar
 * que el orden es otro. Los tiradores de la curva crecen con la distancia: en
 * el abanico de siete son curvas amplias, y entre dos pasos seguidos casi una
 * recta.
 */
function curve(a: Box, b: Box): string {
  const sideways = b.x > a.x + a.w / 2;
  if (sideways) {
    const x1 = a.x + a.w;
    const y1 = a.y + a.h / 2;
    const x2 = b.x;
    const y2 = b.y + b.h / 2;
    const pull = Math.max(40, (x2 - x1) / 2);
    return `M ${x1} ${y1} C ${x1 + pull} ${y1}, ${x2 - pull} ${y2}, ${x2} ${y2}`;
  }
  const x1 = a.x + a.w / 2;
  const y1 = a.y + a.h;
  const x2 = b.x + b.w / 2;
  const y2 = b.y;
  const pull = Math.max(24, (y2 - y1) / 2);
  return `M ${x1} ${y1} C ${x1} ${y1 + pull}, ${x2} ${y2 - pull}, ${x2} ${y2}`;
}
